import apiClient from "./apiClient";

const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;

/** uuid -> Promise<blobUrl> (session-long cache; do not revoke). */
const mediaBlobCache = new Map();

/** Extract media UUID from `/media/{uuid}` (or absolute URL containing it). */
export function extractMediaUuid(ref) {
  if (!ref) return null;
  const value = String(ref).trim();
  if (!value) return null;
  const match = value.match(UUID_RE);
  return match ? match[0] : null;
}

/**
 * Fetch a media file with the auth header and return a blob: URL for <img src>.
 * Results are cached by media UUID for the page session (safe across resizes).
 */
export async function fetchMediaBlobUrl(ref) {
  if (!ref) return "";
  const value = String(ref).trim();
  if (!value) return "";
  if (value.startsWith("blob:") || value.startsWith("data:")) {
    return value;
  }

  const mediaUuid = extractMediaUuid(value);
  if (!mediaUuid) {
    if (/^https?:\/\//i.test(value)) {
      return value;
    }
    return "";
  }

  if (mediaBlobCache.has(mediaUuid)) {
    return mediaBlobCache.get(mediaUuid);
  }

  const request = apiClient
    .get(`/media/${mediaUuid}`, { responseType: "blob" })
    .then((response) => URL.createObjectURL(response.data))
    .catch((err) => {
      mediaBlobCache.delete(mediaUuid);
      throw err;
    });

  mediaBlobCache.set(mediaUuid, request);
  return request;
}

export function invalidateMediaBlob(ref) {
  const mediaUuid = extractMediaUuid(ref);
  if (!mediaUuid || !mediaBlobCache.has(mediaUuid)) return;
  const cached = mediaBlobCache.get(mediaUuid);
  mediaBlobCache.delete(mediaUuid);
  Promise.resolve(cached).then((url) => {
    if (typeof url === "string" && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  });
}

export function clearMediaBlobCache() {
  for (const promise of mediaBlobCache.values()) {
    Promise.resolve(promise).then((url) => {
      if (typeof url === "string" && url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    });
  }
  mediaBlobCache.clear();
}
